import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  useAdminCriarConfiguracaoMutation,
  useAdminMeQuery,
  useAdminVerificarSubdominioQuery,
} from '../../../api';
import { selecionarConfiguracao } from '../../../store/admin-auth-slice';
import { FormaRetiradaCheckbox } from '../../../components/forms/forma-retirada-checkbox';
import { TimezoneSelect } from '../../../components/forms/timezone-select';
import { TIMEZONE_PADRAO } from '../../../lib/timezones-brasil';
import { montarUrlIdentificador } from '../../../lib/url-identificador';
import { AvisoLojaExcluida } from '../../../components/admin/aviso-loja-excluida';

const SUBDOMINIO_MIN = 3;

/**
 * Tela de criação de loja. Usada nos dois cenários:
 *  - **Primeira loja**: redirecionado automaticamente pelo `AdminPage` quando
 *    o admin entra e ainda não tem nenhuma config (URL `?primeira=1`).
 *  - **Loja adicional**: aberta via botão "+ Nova" no Drawer.
 *
 * Além dos dados básicos, já pedimos o **subdomínio do delivery** (o `codigo` de
 * um identificador DELIVERY), validando disponibilidade em tempo real — o campo
 * é UNIQUE global, então precisa ser único.
 */
export function AdminCriarLojaPage(): JSX.Element {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const primeira = params.get('primeira') === '1';
  const { data: me } = useAdminMeQuery();
  const [criar, statusCriar] = useAdminCriarConfiguracaoMutation();

  const [nome, setNome] = useState('');
  const [subdominio, setSubdominio] = useState('');
  const [subdominioDebounced, setSubdominioDebounced] = useState('');
  const [formaRetirada, setFormaRetirada] = useState('DLC');
  const [timezone, setTimezone] = useState(TIMEZONE_PADRAO);
  const [erro, setErro] = useState<string | null>(null);

  // Debounce: só consulta a disponibilidade ~400ms depois de parar de digitar.
  useEffect(() => {
    const t = setTimeout(() => setSubdominioDebounced(subdominio), 400);
    return () => clearTimeout(t);
  }, [subdominio]);

  const { data: dispSub, isFetching: verificandoSub } = useAdminVerificarSubdominioQuery(subdominioDebounced, {
    skip: subdominioDebounced.trim().length < SUBDOMINIO_MIN,
  });

  // "Sincronizado" = o resultado que temos corresponde ao texto atual (não está
  // aguardando o debounce/refetch). Evita habilitar o botão com dado defasado.
  const subSincronizado = subdominio === subdominioDebounced;
  const subDisponivel =
    subSincronizado && !verificandoSub && dispSub?.disponivel === true && subdominio.trim().length >= SUBDOMINIO_MIN;

  if (!me) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (me.idUnidade == null) {
    return (
      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Paper variant="outlined" sx={{ p: 4 }}>
          <Alert severity="error">
            Seu usuário Sischef não tem uma Unidade vinculada. Cadastre a Unidade no Sischef
            antes de criar uma loja no cardápio digital.
          </Alert>
        </Paper>
      </Container>
    );
  }

  function onChangeSubdominio(valor: string): void {
    // Normaliza para slug enquanto digita: minúsculas, só letras/números/hífen.
    setSubdominio(valor.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  }

  function helperSubdominio(): { texto: string; erro: boolean } {
    const tam = subdominio.trim().length;
    if (tam === 0) return { texto: 'Será o endereço do delivery. Ex.: boteco', erro: false };
    if (tam < SUBDOMINIO_MIN) return { texto: `Mínimo de ${SUBDOMINIO_MIN} caracteres.`, erro: false };
    if (verificandoSub || !subSincronizado) return { texto: 'Verificando disponibilidade...', erro: false };
    if (dispSub && !dispSub.disponivel) return { texto: dispSub.mensagem ?? 'Indisponível', erro: true };
    if (subDisponivel) return { texto: 'Disponível ✓', erro: false };
    return { texto: 'Será o endereço do delivery. Ex.: boteco', erro: false };
  }

  async function confirmar(): Promise<void> {
    setErro(null);
    if (!formaRetirada) {
      setErro('Escolha pelo menos uma forma de retirada');
      return;
    }
    if (!subDisponivel) {
      setErro('Escolha um subdomínio de delivery disponível');
      return;
    }
    try {
      const criada = await criar({
        nome: nome.trim(),
        subdominio: subdominio.trim(),
        formaRetirada,
        timezone: timezone || TIMEZONE_PADRAO,
      }).unwrap();
      dispatch(selecionarConfiguracao(criada.id));
      navigate('/admin/configuracao', { replace: true });
    } catch (err) {
      const msg = (err as { data?: { mensagem?: string } })?.data?.mensagem;
      setErro(msg ?? 'Falha ao criar loja');
    }
  }

  const subHelper = helperSubdominio();

  return (
    <Container maxWidth="sm" sx={{ pt: { xs: 2, md: 4 } }} disableGutters>
      <AvisoLojaExcluida />
      <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
        <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <StorefrontIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" align="center" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            {primeira ? 'Vamos criar sua primeira loja' : 'Nova loja'}
          </Typography>
          {primeira && (
            <Typography color="text.secondary" align="center" variant="body2">
              Detectamos que você ainda não tem nenhum cardápio digital configurado para a
              Unidade #{me.idUnidade}. Preencha os dados básicos abaixo — você pode ajustar
              tudo depois nas configurações.
            </Typography>
          )}
        </Stack>

        <Stack spacing={2}>
          <TextField
            label="Nome da loja"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Boteco do Zé — Centro"
            fullWidth
            autoFocus
            required
          />

          <Box>
            <TextField
              label="Subdomínio do delivery"
              value={subdominio}
              onChange={(e) => onChangeSubdominio(e.target.value)}
              placeholder="boteco"
              fullWidth
              required
              error={subHelper.erro}
              helperText={subHelper.texto}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {verificandoSub || !subSincronizado ? (
                      <CircularProgress size={16} />
                    ) : subDisponivel ? (
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                    ) : null}
                  </InputAdornment>
                ),
              }}
            />
            {subdominio.trim().length >= SUBDOMINIO_MIN && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                Endereço: {montarUrlIdentificador({ tipo: 'DELIVERY', codigo: subdominio.trim() })}
              </Typography>
            )}
          </Box>

          <FormaRetiradaCheckbox valor={formaRetirada} onChange={setFormaRetirada} />
          <TimezoneSelect valor={timezone} onChange={setTimezone} />
          {erro && <Alert severity="error">{erro}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            {!primeira && (
              <Button onClick={() => navigate(-1)} disabled={statusCriar.isLoading}>
                Cancelar
              </Button>
            )}
            <Button
              variant="contained"
              onClick={confirmar}
              disabled={!nome.trim() || !subDisponivel || statusCriar.isLoading}
            >
              {statusCriar.isLoading ? <CircularProgress size={20} /> : 'Criar loja'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
